/**
 * Generated JSX Preview Actions
 *
 * Safe action components for AI-generated JSX previews.
 *
 * Primary responsibilities:
 * - Provide local-only actions for generated preview buttons
 * - Keep generated UI interactivity out of arbitrary inline handlers
 *
 * Dependencies:
 * - Button
 * - safe URL helpers
 */

'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode
} from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { Button, type ButtonProps } from '@/components/ui/button'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import { cn } from '@/lib/utils'
import { getSafeMarkdownUrl } from '@/lib/media/safe-url'
import {
  GetPremiumMarkdownButton,
  type GetPremiumMarkdownButtonProps
} from '@/components/messages/get-premium-markdown-button'

type PreviewValue = boolean | string

type GeneratedPreviewActionContextValue = {
  copiedKey: string | null
  copyText: (text: string, key: string) => Promise<boolean>
  openHref: (href: string) => boolean
  setValue: (target: string, value: PreviewValue) => void
  toggleValue: (target: string) => void
  values: Record<string, PreviewValue>
}

const GeneratedPreviewActionContext =
  createContext<GeneratedPreviewActionContextValue | null>(null)

const COPY_STATE_TIMEOUT_MS = 1600

// --------------

function useGeneratedPreviewActions() {
  const context = useContext(GeneratedPreviewActionContext)

  if (!context) {
    throw new Error('Generated preview actions must be used inside the provider')
  }

  return context
}

// --------------

function getOpenHref(value: string) {
  const href = getSafeMarkdownUrl(value)
  if (!href) return ''

  if (
    /^https?:\/\//i.test(href) ||
    /^mailto:/i.test(href) ||
    href.startsWith('#') ||
    href.startsWith('/') ||
    href.startsWith('./') ||
    href.startsWith('../')
  ) {
    return href
  }

  return ''
}

// --------------

export function GeneratedPreviewActionsProvider({
  children
}: {
  children: ReactNode
}) {
  const [values, setValues] = useState<Record<string, PreviewValue>>({})
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  const setValue = useCallback((target: string, value: PreviewValue) => {
    setValues((current) => ({
      ...current,
      [target]: value
    }))
  }, [])

  const toggleValue = useCallback((target: string) => {
    setValues((current) => ({
      ...current,
      [target]: !Boolean(current[target])
    }))
  }, [])

  const copyText = useCallback(async (text: string, key: string) => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return false

    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)

      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedKey(null)
        copyTimeoutRef.current = null
      }, COPY_STATE_TIMEOUT_MS)

      return true
    } catch {
      return false
    }
  }, [])

  const openHref = useCallback((value: string) => {
    const href = getOpenHref(value)
    if (!href) return false

    if (href.startsWith('#')) {
      try {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' })
      } catch {
        return false
      }
      return true
    }

    window.open(href, '_blank', 'noopener,noreferrer')
    return true
  }, [])

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    },
    []
  )

  const contextValue = useMemo(
    () => ({
      copiedKey,
      copyText,
      openHref,
      setValue,
      toggleValue,
      values
    }),
    [copiedKey, copyText, openHref, setValue, toggleValue, values]
  )

  return (
    <GeneratedPreviewActionContext.Provider value={contextValue}>
      {children}
    </GeneratedPreviewActionContext.Provider>
  )
}

// --------------

type GeneratedActionButtonAction = 'copy' | 'download' | 'export' | 'open' | 'set' | 'toggle'

export interface GeneratedActionButtonProps
  extends Omit<ButtonProps, 'onClick' | 'type' | 'value'> {
  action?: GeneratedActionButtonAction
  copiedLabel?: ReactNode
  href?: string
  target?: string
  text?: string
  value?: string
}

export function GeneratedActionButton({
  action,
  children,
  copiedLabel = 'Copied',
  href,
  target,
  text,
  value,
  variant,
  ...props
}: GeneratedActionButtonProps) {
  const { copiedKey, copyText, openHref, setValue, toggleValue, values } =
    useGeneratedPreviewActions()
  const actionValue = text ?? value ?? ''
  const copyKey = `${target ?? 'copy'}:${actionValue}`
  const isCopied = action === 'copy' && copiedKey === copyKey
  const isPressed =
    target && action === 'toggle'
      ? Boolean(values[target])
      : Boolean(target && action === 'set' && values[target] === actionValue)

  const handleClick = useCallback(async () => {
    if (action === 'copy') {
      const copied = await copyText(actionValue, copyKey)
      if (copied) {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: 'copied',
          source: 'generated_preview'
        })
      }
      return
    }

    if (action === 'open' || action === 'download' || action === 'export') {
      const opened = openHref(href ?? actionValue)
      if (opened) {
        void trackMeaningfulOutcomeCompleted({
          surface: 'chat',
          outcome_type: 'saved_output',
          qualifying_action: action === 'open' ? 'reopened' : 'exported',
          source: 'generated_preview'
        })
      }
      return
    }

    if (action === 'toggle' && target) {
      toggleValue(target)
      return
    }

    if (action === 'set' && target) {
      setValue(target, actionValue || true)
    }
  }, [
    action,
    actionValue,
    copyKey,
    copyText,
    href,
    openHref,
    setValue,
    target,
    toggleValue
  ])

  return (
    <Button
      aria-pressed={target && (action === 'toggle' || action === 'set') ? isPressed : undefined}
      onClick={action ? handleClick : undefined}
      type="button"
      variant={variant ?? (isPressed ? 'secondary' : undefined)}
      {...props}
    >
      {action === 'copy' ? (
        isCopied ? (
          <Check className="size-4" />
        ) : (
          <Copy className="size-4" />
        )
      ) : null}
      {action === 'open' ? <ExternalLink className="size-4" /> : null}
      {isCopied ? copiedLabel : children}
    </Button>
  )
}

// --------------

export function GeneratedCopyButton({
  children = 'Copy',
  ...props
}: Omit<GeneratedActionButtonProps, 'action'>) {
  return (
    <GeneratedActionButton action="copy" {...props}>
      {children}
    </GeneratedActionButton>
  )
}

// --------------

export function GeneratedOpenButton({
  children = 'Open',
  ...props
}: Omit<GeneratedActionButtonProps, 'action'>) {
  return (
    <GeneratedActionButton action="open" {...props}>
      {children}
    </GeneratedActionButton>
  )
}

// --------------

export function GeneratedStateButton({
  children,
  ...props
}: Omit<GeneratedActionButtonProps, 'action'>) {
  return (
    <GeneratedActionButton action="set" {...props}>
      {children}
    </GeneratedActionButton>
  )
}

// --------------

export function GeneratedToggleButton({
  children,
  ...props
}: Omit<GeneratedActionButtonProps, 'action'>) {
  return (
    <GeneratedActionButton action="toggle" {...props}>
      {children}
    </GeneratedActionButton>
  )
}

// --------------

export function GeneratedPremiumButton({
  children,
  label,
  ...props
}: GetPremiumMarkdownButtonProps & { children?: ReactNode }) {
  const childLabel =
    typeof children === 'string' || typeof children === 'number'
      ? String(children)
      : undefined

  return (
    <GetPremiumMarkdownButton label={label ?? childLabel} {...props} />
  )
}

// --------------

export interface GeneratedActionPanelProps extends ComponentProps<'div'> {
  defaultOpen?: boolean
  target?: string
  value?: string
}

export function GeneratedActionPanel({
  children,
  className,
  defaultOpen = false,
  target,
  value,
  ...props
}: GeneratedActionPanelProps) {
  const { values } = useGeneratedPreviewActions()
  const currentValue = target ? values[target] : undefined
  const isVisible = target
    ? currentValue === undefined
      ? defaultOpen
      : value === undefined
        ? Boolean(currentValue)
        : currentValue === value
    : defaultOpen

  if (!isVisible) return null

  return (
    <div className={cn('mt-3', className)} {...props}>
      {children}
    </div>
  )
}

// --------------

export interface GeneratedActionViewProps extends ComponentProps<'div'> {
  defaultActive?: boolean
  target: string
  value: string
}

export function GeneratedActionView({
  children,
  className,
  defaultActive = false,
  target,
  value,
  ...props
}: GeneratedActionViewProps) {
  const { values } = useGeneratedPreviewActions()
  const currentValue = values[target]
  const isVisible = currentValue === undefined ? defaultActive : currentValue === value

  if (!isVisible) return null

  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}
